/**
 * Pure equipment engine (Phase 2 contract).
 *
 * Equipping removes exactly one item from its source slot. Displaced
 * equipment returns to the inventory if it fits — otherwise the whole
 * operation aborts atomically. Unequip likewise returns to the inventory
 * or aborts. Nothing is ever created or destroyed.
 */
import { ITEMS } from '../../data';
import type { EquipSlotId, ItemId, PlayerEquipment } from '../state';
import {
  getSlot,
  isValidRef,
  placeInInventory,
  withSlot,
  type Containers,
  type SlotRef,
} from './containers';

export type EquipBonuses = { attack: number; defence: number; hp: number };

export type EquipFailureReason = 'invalid-slot' | 'empty-source' | 'not-equippable' | 'no-space';

export type EquipmentState = { containers: Containers; equipment: PlayerEquipment };

export type EquipResult =
  | ({ ok: true } & EquipmentState)
  | ({ ok: false; reason: EquipFailureReason } & EquipmentState);

/** The compatible equipment slot an item declares, or null. */
export function equipSlotFor(itemId: ItemId): EquipSlotId | null {
  return ITEMS.items[itemId]?.equip?.slot ?? null;
}

export function itemBonuses(itemId: ItemId): EquipBonuses {
  const bonuses = ITEMS.items[itemId]?.equip?.bonuses;
  return {
    attack: bonuses?.attack ?? 0,
    defence: bonuses?.defence ?? 0,
    hp: bonuses?.hp ?? 0,
  };
}

export function totalBonuses(equipment: PlayerEquipment): EquipBonuses {
  return Object.values(equipment).reduce<EquipBonuses>(
    (acc, itemId) => {
      if (!itemId) return acc;
      const b = itemBonuses(itemId);
      return { attack: acc.attack + b.attack, defence: acc.defence + b.defence, hp: acc.hp + b.hp };
    },
    { attack: 0, defence: 0, hp: 0 },
  );
}

/** Equip one unit from a bank/inventory slot into its declared equipment slot. */
export function equipFrom(state: EquipmentState, from: SlotRef): EquipResult {
  const fail = (reason: EquipFailureReason): EquipResult => ({ ok: false, reason, ...state });

  if (!isValidRef(from)) return fail('invalid-slot');
  const source = getSlot(state.containers, from);
  if (!source) return fail('empty-source');

  const slot = equipSlotFor(source.itemId);
  if (!slot) return fail('not-equippable');

  // Remove exactly one from the source first so a qty-1 source frees its slot
  // for the displaced item.
  const remainder = source.qty - 1;
  let containers = withSlot(
    state.containers,
    from,
    remainder > 0 ? { itemId: source.itemId, qty: remainder } : null,
  );

  const displaced = state.equipment[slot];
  if (displaced) {
    const inventory = placeInInventory(containers.inventory, displaced, 1);
    if (inventory === null) return fail('no-space');
    containers = { ...containers, inventory };
  }

  return { ok: true, containers, equipment: { ...state.equipment, [slot]: source.itemId } };
}

/** Return an equipped item to the inventory, or abort if it cannot fit. */
export function unequip(state: EquipmentState, slot: EquipSlotId): EquipResult {
  const fail = (reason: EquipFailureReason): EquipResult => ({ ok: false, reason, ...state });

  const itemId = state.equipment[slot];
  if (!itemId) return fail('empty-source');

  const inventory = placeInInventory(state.containers.inventory, itemId, 1);
  if (inventory === null) return fail('no-space');

  return {
    ok: true,
    containers: { ...state.containers, inventory },
    equipment: { ...state.equipment, [slot]: null },
  };
}
